/**
 * focusModel — what the focus explorer shows, derived from the blueprint.
 *
 * One focused module produces a STRIP of top-down views:
 *   [0]  the focus itself, on its own page;
 *   [1…] one per connection the focus takes part in — the module at the OTHER
 *        end, on ITS own page (which is often a different page), captioned
 *        with the relationship.
 *
 * Each view names two sets:
 *   keep  the module plus its ancestors up to the page root — the chain that
 *         must stay legible for the module to be findable on the page;
 *   dim   the FRONTIER to gray out: the children of the kept chain that are
 *         not themselves kept. Graying every non-kept node instead would nest
 *         opacities (0.2 × 0.2 × …) until a deep subtree disappeared; graying
 *         only the outermost ones fades each sibling exactly once.
 *
 * Pure — no React, no DOM.
 */

/** child id → parent id, over `children` and a page's `sections[].component`. */
export function parentMap(blueprint) {
  const comps = blueprint?.components || {};
  const parent = new Map();
  for (const [id, node] of Object.entries(comps)) {
    for (const kid of node.children || []) {
      if (typeof kid === 'string') parent.set(kid, id);
    }
    for (const s of node.props?.sections || []) {
      if (s?.component) parent.set(s.component, id);
    }
  }
  return parent;
}

/**
 * A rendered card has a synthetic per-item id `<gridId>__card__<itemId>` (see
 * collections.jsx). It has no entry in `components`, so its owning grid — the
 * declared node that carries its `props.card` template — is parsed from the id.
 */
const CARD_SEP = '__card__';
const LIFECYCLE_SEP = '__pending__';
export function cardOwner(nodeId) {
  const i = String(nodeId).indexOf(CARD_SEP);
  return i < 0 ? null : String(nodeId).slice(0, i);
}

/** A framework-generated PendingCard belongs to the collection that injected it. */
export function lifecycleOwner(nodeId) {
  const i = String(nodeId).indexOf(LIFECYCLE_SEP);
  return i < 0 ? null : String(nodeId).slice(0, i);
}

/**
 * The DECLARED node a runtime id speaks for — itself when the blueprint
 * declares it, otherwise the grid that laid it out. Every card instance and
 * every node INSIDE a card template (`<grid>__card__<item>__child0__child1`)
 * resolves to the grid: the template's leaves have no blueprint entry of their
 * own, so keying data and wiring on the raw id came up empty and the explorer
 * called a bound, wired row "rendered, never wired".
 */
export function declaredOwner(blueprint, nodeId) {
  const comps = blueprint?.components || {};
  if (comps[nodeId]) return nodeId;
  const grid = cardOwner(nodeId) || lifecycleOwner(nodeId);
  return grid && comps[grid] ? grid : null;
}
/** A card's component type comes from its grid's `props.card.type`. */
function cardType(comps, nodeId) {
  const g = cardOwner(nodeId);
  return g ? comps[g]?.props?.card?.type || null : null;
}

/** The component type a runtime node id renders as, or null if unknowable. */
export function typeOfNode(blueprint, nodeId) {
  const comps = blueprint?.components || {};
  if (comps[nodeId]) return comps[nodeId].type;
  if (lifecycleOwner(nodeId)) return 'PendingCard';
  // A card INSTANCE is a Card ROW, whatever primitive its template happens to
  // root in — scene-studio's clip template roots in a Stack, art-studio's in an
  // Overlay. That root is the template's construction, never the answer to "what
  // did I just select": selecting a card lands on a Card. (A leaf INSIDE the
  // template still reports the root type — it is not an instance.)
  if (isCardInstance(nodeId)) return 'Card';
  return cardType(comps, nodeId) || null;
}

/**
 * Pure CONTAINERS. They lay other modules out and render nothing of their own,
 * so "the Stack at this point" is never the answer to "what did I just tap" —
 * a section wrapper (`clipSection`) and a card template's root are both a bare
 * `Stack`, and focusing one told you nothing the page had not already shown.
 *
 * Named types, not a registry lookup: the registry is fetched, the studio
 * resolves taps before it lands, and this set is L1 vocabulary that does not
 * move. `Grid` is deliberately absent — a raw grid is a real arrangement
 * decision, and its bound cousins (the collections) are modules.
 */
export const LAYOUT_TYPES = new Set(['Stack', 'Overlay', 'Surface']);

/**
 * A card INSTANCE — `<grid>__card__<itemId>` with no `__child…` tail. It is a
 * ROW, not a wrapper: it carries the item's identity, which is the whole reason
 * the studio can light a data row from it and target a wire at it.
 */
export function isCardInstance(nodeId) {
  const s = String(nodeId ?? '');
  const i = s.indexOf(CARD_SEP);
  return i >= 0 && !s.slice(i + CARD_SEP.length).includes('__child');
}

/** A generated lifecycle cell, excluding any synthetic node nested inside it. */
export function isLifecycleInstance(nodeId) {
  const s = String(nodeId ?? '');
  const i = s.indexOf(LIFECYCLE_SEP);
  if (i < 0) return false;
  const tail = s.slice(i + LIFECYCLE_SEP.length);
  return !/__(child|composed|r\d)/.test(tail);
}

/** Either per-row runtime surface a collection lays out. */
export function isCollectionCellInstance(nodeId) {
  return isCardInstance(nodeId) || isLifecycleInstance(nodeId);
}

export function isLayoutNode(blueprint, nodeId) {
  // A card instance is never a pure container, whatever its TEMPLATE's root
  // element happens to be. `cardType` reads that root off the grid's
  // `props.card.type`, and 9 of the fleet's 20 card templates root in an
  // `Overlay` or a `Stack` (art-studio, doc-digitizer, plant-expert,
  // scene-studio, social-media-studio) — so the skip walked the tap straight
  // past the card and onto the grid, which is the one thing a tap on a card
  // cannot mean. The `Card`-rooted widgets never showed it, which is why this
  // read as widget-specific rather than as the layout skip overshooting.
  if (isCollectionCellInstance(nodeId)) return false;
  return LAYOUT_TYPES.has(typeOfNode(blueprint, nodeId));
}

/**
 * The SYNTHETIC tails the interpreter appends to a declared id when it renders
 * something the blueprint never named: an INLINE child object
 * (`plantHero__child0`), one iteration of a repeat (`x__r2`), a kit
 * composition's body (`y__composed`). See `interpreter.jsx` — `childId`.
 */
const SYNTHETIC_TAIL = /__(child|composed|r\d)/;

/**
 * The id a TAP on this runtime node should speak for.
 *
 * A node the blueprint never declared has no identity of its own, so it speaks
 * for the one that declares it. Two shapes, one rule:
 *   · a leaf inside a card template (`<grid>__card__<item>__child0__child1`)
 *     collapses to the CARD, which is a real row;
 *   · anything else synthetic (`plantHero__child0` — the plant page's hero
 *     IMAGE, written inline inside its Overlay) collapses to the declared node
 *     it hangs off.
 *
 * The second case is why tapping the plant photo in the focus zone NAVIGATED
 * the studio to the entry page: `plantHero__child0` has no parent in the
 * blueprint, so `ancestorsOf` stopped at itself, no page's root matched, and
 * `pageOfNode`'s last-resort fallback answered `index` — which the pane then
 * wrote into `?page=`. A tap on a photo is a selection of `plantHero`, which
 * is on the plant page, and nothing moves.
 *
 * FAIL CLOSED: the collapsed prefix is taken only when the blueprint really
 * declares it. An id this rule cannot land on a real node is left exactly as it
 * is rather than replaced with a guess.
 */
export function focusTargetOf(blueprint, nodeId) {
  if (!nodeId) return null;
  if (blueprint?.components?.[nodeId]) return nodeId;
  const grid = cardOwner(nodeId);
  if (grid) {
    const rest = String(nodeId).slice(grid.length + CARD_SEP.length);
    const cut = rest.indexOf('__child');
    return cut < 0 ? nodeId : `${grid}${CARD_SEP}${rest.slice(0, cut)}`;
  }
  const lifecycleGrid = lifecycleOwner(nodeId);
  if (lifecycleGrid) {
    const rest = String(nodeId).slice(lifecycleGrid.length + LIFECYCLE_SEP.length);
    const cut = rest.search(SYNTHETIC_TAIL);
    return cut < 0 ? nodeId : `${lifecycleGrid}${LIFECYCLE_SEP}${rest.slice(0, cut)}`;
  }
  const cut = String(nodeId).search(SYNTHETIC_TAIL);
  if (cut < 0) return nodeId;
  const owner = String(nodeId).slice(0, cut);
  return blueprint?.components?.[owner] ? owner : nodeId;
}

/** The stacked ids a tap could mean, innermost → outermost, deduped. */
export function focusCandidates(blueprint, ids) {
  const out = [];
  for (const raw of ids || []) {
    const id = focusTargetOf(blueprint, raw);
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * WHAT A TAP MEANS, given the ids stacked under the point (innermost →
 * outermost) and what is focused now. Returns the id to focus, or null to
 * release.
 *
 *   nothing focused / a DIFFERENT module  → the innermost MEANINGFUL node
 *   the focused module tapped again       → the next meaningful node OUTWARD
 *
 * "Meaningful" = not a pure container — and a CARD INSTANCE always counts,
 * however its template is rooted. Tapping a clip lands on the clip
 * (`clipRail__card__<id>`), and tapping again walks out to the `clipRail` that
 * lays those clips out. It used to skip straight to the rail whenever the
 * template rooted in an Overlay or a Stack, which is most of the fleet — the
 * card you aimed at was never selectable, so the row's identity (and the data
 * pick that hangs off it) was unreachable by tapping. Falls back to the
 * innermost id when a point covers nothing but containers, so a tap never does
 * nothing.
 */
export function tapTarget(blueprint, ids, focus = null) {
  const cands = focusCandidates(blueprint, ids);
  if (!cands.length) return null;
  const meaningful = (list) => list.find((id) => !isLayoutNode(blueprint, id)) ?? null;
  if (focus == null) return meaningful(cands) ?? cands[0];
  const k = cands.indexOf(focus);
  if (k === -1) return meaningful(cands) ?? cands[0];
  // Lifecycle cells are injected by the focused collection, so the normal
  // "tap the focus again → walk outward" rule would otherwise make them
  // impossible to enter: the owner is already later in the same hit stack.
  // Landed-card traversal stays byte-for-byte as before; this exception is
  // only for the framework surface the author explicitly aimed at.
  const innerLifecycle = cands.slice(0, k).find((id) => isLifecycleInstance(id));
  if (innerLifecycle && declaredOwner(blueprint, innerLifecycle) === focus) return innerLifecycle;
  return meaningful(cands.slice(k + 1));
}

/** The node, then every ancestor up to (and including) the page root. */
export function ancestorsOf(blueprint, nodeId) {
  const parent = parentMap(blueprint);
  const chain = [];
  let cur = nodeId;
  const seen = new Set();
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    chain.push(cur);
    // a card walks up to its owning grid, which IS in the parent map
    cur = parent.get(cur) ?? cardOwner(cur) ?? lifecycleOwner(cur) ?? undefined;
  }
  return chain;
}

/** The page whose tree contains this node (the entry page wins a tie). */
export function pageOfNode(blueprint, nodeId) {
  const pages = blueprint?.pages || [];
  const root = ancestorsOf(blueprint, nodeId).at(-1);
  const owning = pages.filter((p) => p.root === root);
  return owning.find((p) => p.entry) || owning[0] || pages.find((p) => p.entry) || pages[0] || null;
}

/**
 * The two host slots that open a page OF THIS SAME BUNDLE, and the `page_args`
 * key each one is opened with. Everything else the host does — `openCapture`,
 * `crudRemove`, `itemOptions`, `imageTap`, `openMainSession`, `applyUpdate` —
 * either leaves the widget entirely or performs an action with no UI of its
 * own, and stays a bridge with nothing to draw.
 */
const HOST_PAGE_SLOTS = {
  openItem: { bind: 'focusEntry', argKey: 'item_id' },
  openSession: { bind: 'focusGrid', argKey: 'session_id' },
};

/**
 * THE THREE ROOTS THAT NAME THE BOUND ITEM rather than a selector.
 *
 * `focusEntry` is the row a detail page was opened on (`page_args.item_id`),
 * `focusGrid` the capture a session page was opened on (`page_args.session_id`),
 * and `item` the row of whatever scope encloses the reference. They are DATA —
 * scene-studio's `stillStage` renders `focusEntry.images` and nothing else —
 * but they are not in `data.selectors`, so the studio's whole data model was
 * blind to them and a detail page's modules read as "rendered, never wired".
 */
export const ITEM_BINDINGS = ['focusEntry', 'focusGrid', 'item'];
const ITEM_BINDING_SET = new Set(ITEM_BINDINGS);

/** A component's bound selector name, whichever of the two spellings it uses. */
function bindOf(node) {
  const raw = typeof node?.bind === 'string' ? node.bind : node?.bind?.bind;
  return typeof raw === 'string' ? raw.split('.')[0].trim() : null;
}

/**
 * `host.<slot>` → the page THIS BUNDLE draws when the host performs it, or null.
 *
 * "Tap a card → the host opens the detail page" is the single most common thing
 * a widget does, and the strip used to render it as a dead "host bridge · no
 * UI" chip: the destination page is part of the same blueprint, so refusing to
 * draw it hid the whole second half of the interaction.
 *
 * WHICH page it is, is DECLARED, never named: the host resolves an item-bound
 * page through `focusEntry` and a session-bound one through `focusGrid` (see
 * `viewir/context`), so the destination is whichever page resolves that binding.
 * The binding may live on the root or on a state branch inside it; using the
 * same page-level resolver as the preview keeps both shapes equivalent without
 * hardcoding a page name.
 *
 * The ENTRY page is never a destination: it is the page you are already on.
 */
export function hostOpensPage(blueprint, slot) {
  const want = HOST_PAGE_SLOTS[slot];
  if (!want) return null;
  for (const page of blueprint?.pages || []) {
    if (!page || page.entry) continue;
    // 复用页面级绑定解析，让根节点和内部状态分支的详情页具有相同语义。
    if (pageFocusBinding(blueprint, page) === want.bind) {
      return { pageId: page.id, argKey: want.argKey };
    }
  }
  return null;
}

/**
 * The `page_args` a host-opened page is opened WITH — grounded in the ROW, not
 * in the id the card happened to be keyed by.
 *
 * A card's node id is `<grid>__card__<key>`, and `key` is whatever identity its
 * grid laid it out by: a `SessionCollection` keys by the SESSION (`s-ghibli`),
 * so opening an item page on that key asks for a creation id that the row does
 * not answer to under that name. The row knows both spellings, so each argument
 * takes its own: `item_id` is the creation (`item_id || id`), `session_id` is
 * the capture (`session_id || grid_id || source_session_id`).
 *
 * FAIL CLOSED, one rung at a time: no row → `fallbackKey`, the identity the
 * card was tapped with, which is at least right for one of the two; nothing at
 * all → null, and `focusEntry` / `focusGrid` resolve their own miss exactly as
 * they do for a host that opened the page with no arguments.
 */
export function hostPageArgs(argKey, row = null, fallbackKey = null) {
  const fromRow = argKey === 'session_id'
    ? (row?.session_id ?? row?.grid_id ?? row?.source_session_id)
    : (row?.item_id ?? row?.id);
  const value = fromRow ?? fallbackKey ?? null;
  return value === null || value === undefined || value === '' ? null : { [argKey]: String(value) };
}

/** Children of the kept chain that are not kept — the outermost grays. */
export function dimFrontier(blueprint, keep) {
  const comps = blueprint?.components || {};
  const dim = new Set();
  for (const id of keep) {
    const node = comps[id];
    if (!node) continue;
    for (const kid of node.children || []) {
      if (typeof kid === 'string' && !keep.has(kid)) dim.add(kid);
    }
    for (const s of node.props?.sections || []) {
      if (s?.component && !keep.has(s.component)) dim.add(s.component);
    }
  }
  return dim;
}

/** One top-down view: which page to render, what to keep, what to gray. */
function viewOf(blueprint, nodeId, extra = {}) {
  const keep = new Set(ancestorsOf(blueprint, nodeId));
  return {
    nodeId,
    type: typeOfNode(blueprint, nodeId) || (nodeId === 'host' ? 'host' : '?'),
    page: pageOfNode(blueprint, nodeId),
    keep,
    dim: dimFrontier(blueprint, keep),
    ...extra,
  };
}

/** `"shelves.itemActivated"` → `["shelves", "itemActivated"]`. */
function split(ref) {
  const s = String(ref || '');
  const i = s.indexOf('.');
  return i < 0 ? [s, ''] : [s.slice(0, i), s.slice(i + 1)];
}

/**
 * The strip for one focus: itself first, then one view per connection it takes
 * part in, in blueprint order. The host has no page of its own, so its views
 * are flagged `kind: 'host'` for the renderer to draw as a bridge chip.
 *
 * A CARD is the one focus that does not take the whole set. Its grid answers
 * for two different things at once — the ROW you tapped, and the CONTAINER that
 * lays every row out — and only the first is what a card is:
 *
 *   the grid EMITS (`itemActivated`, `removeRequested`, `itemSelected`)
 *     → the card's own doing, re-emitted upward with the row's identity: KEPT;
 *   the grid RECEIVES (`applyFilter`, `applyQuery`, `setSelected`, …)
 *     → something done to the WHOLE list by a filter bar or the host, which no
 *       single row emits and no single row receives: DROPPED, and still there
 *       on the grid's own focus, where it is a real connection of that module.
 *
 * Focusing an art-studio tile used to lead with `mediaChips.tabChanged →
 * tileGrid.applyFilter` — the strip's first card, and the wire it draws — when
 * what tapping that tile does is open the piece.
 */
export function focusViews(blueprint, focusId) {
  if (!focusId) return [];
  const views = [viewOf(blueprint, focusId, {
    key: `focus-${focusId}`,
    kind: 'focus',
    caption: 'in focus',
  })];
  // A laid-out card owns no wires of its own: the GRID re-emits whatever the
  // card emitted (with the row's identity attached) and every slot pushed at
  // the card is pushed at the grid. So a card node takes part in exactly the
  // connections its grid does — plus any its own declared id has. A declared
  // node has no owner and matches only itself, so nothing else moves.
  const owner = declaredOwner(blueprint, focusId);
  const isFocus = (id) => id === focusId || (owner !== null && id === owner);
  const card = isCollectionCellInstance(focusId);
  const seen = new Set();
  const wires = [];
  (blueprint?.connections || []).forEach((c, i) => {
    if (!c?.from || !c?.to) return;
    const [fromId, signal] = split(c.from);
    const [toId, slot] = split(c.to);
    if (!isFocus(fromId) && !isFocus(toId)) return;
    const outgoing = isFocus(fromId);
    // the container's incoming wiring belongs to the grid, not to one row
    if (card && !outgoing) return;
    const other = outgoing ? toId : fromId;
    const caption = outgoing ? `▸ ${signal} → ${other}.${slot}` : `◂ ${other}.${signal} → ${slot}`;
    const key = `${other}|${caption}`;
    if (seen.has(key)) return;
    seen.add(key);
    wires.push({
      // A wire that hands the row to a host slot which opens a page OF THIS
      // BUNDLE (`hostOpensPage`) is what tapping the card MEANS, so it leads
      // and the strip's default wire draws that destination page. Which slot
      // that is stays declared, never named here: art-studio and medicine-box
      // reach their detail page through `openItem`, my-wardrobe its photo page
      // through `openSession`, and scene-studio's `openSession` opens no page
      // of ours at all — so it does not lead, and must not.
      lead: card && other === 'host' && hostOpensPage(blueprint, slot) !== null,
      view: viewOf(blueprint, other, {
        key: `w${i}-${other}`,
        kind: other === 'host' ? 'host' : 'wired',
        caption,
        // Which way the data really flows: `out` = the focus emits and the other
        // end receives; `in` = the other end emits into the focus. The arrowhead
        // follows this, never the layout.
        dir: outgoing ? 'out' : 'in',
        mode: c.mode || 'async',
        executor: c.executor || 'ui',
        // the wire's own label: what leaves, and what it lands in
        signal,
        slot,
        // THE CONNECTION'S OWN PAYLOAD MAP (§5) and the module that EMITS it.
        // A host slot with no page of ours behind it is not always "no UI —
        // the app performs it": four of them open an agent SESSION, and this
        // payload is the whole of what that session receives. Which module
        // emitted it decides the signal's shape (an OptionSelect sends the
        // clicked option's own payload), so the id travels with it —
        // `focusId` is a card instance, and only the DECLARED node has props.
        payload: c.payload ?? null,
        emitter: fromId,
        emitterType: typeOfNode(blueprint, fromId),
      }),
    });
  });
  // stable, so everything that does not open a page keeps blueprint order
  if (card) wires.sort((a, b) => Number(b.lead) - Number(a.lead));
  for (const w of wires) views.push(w.view);
  return views;
}

/**
 * Where to start: the entry page's busiest module (the one the most
 * connections touch), falling back to the page root. That lands you on the
 * part of the widget the wiring actually revolves around.
 */
export function defaultFocus(blueprint) {
  const pages = blueprint?.pages || [];
  const entry = pages.find((p) => p.entry) || pages[0];
  if (!entry) return null;
  const comps = blueprint?.components || {};
  const onPage = Object.keys(comps).filter((id) => pageOfNode(blueprint, id)?.id === entry.id);
  const degree = (id) => (blueprint?.connections || []).filter(
    (c) => split(c.from)[0] === id || split(c.to)[0] === id,
  ).length;
  let best = entry.root;
  for (const id of onPage) if (degree(id) > degree(best)) best = id;
  return best;
}

/**
 * EVERY selector one component node references, in every form the IR spells a
 * reference — deduped, in the order they are met.
 *
 * "As long as a module uses data, it draws a connection." The scan used to be
 * `bind` plus the TOP LEVEL of `props`, so a reference one level down was
 * invisible: scene-studio's `clipHead` counts `videoReady` through
 * `props.count = {agg:{op:'count', of:'videoReady'}}`, its `summary` reads
 * three selectors through `props.items[].value.agg.of`, and a `when` clause is
 * a data dependency the scan did not look at at all. Every one of those
 * modules reported "reads nothing" and drew no data connection while plainly
 * rendering a number off a selector.
 *
 * THE POSITIONS A BARE STRING IS A REFERENCE are `bind`, `when`, and the top
 * level of `props` — that is the authored contract (`bind: "shelves"`,
 * `when: "posts"`, `props.options: "modeFacets"`). DEEPER DOWN A BARE STRING IS
 * A LITERAL and is deliberately not taken: a `ModeSwitch`'s `tabs[].key` is
 * "outfits", an `ItemPage`'s `fields[]` lists "safety", an aggregate's
 * `suffix` is " pieces" — and in the fleet as it stands each of those spells an
 * existing selector's name exactly. Taking them would say a pill switch reads
 * the outfits list, which is the one claim this map must never make (it is what
 * the data zone's `→ module` badges and the explorer's data wire both rest on).
 *
 * So nesting is walked for the TYPED envelopes instead, at any depth and inside
 * arrays: `{agg:{op,of|from}}` (an aggregate names its source), `{$: "sel.path"}`
 * (an interpolation) and `{path: "sel…", op, value}` (a when clause).
 *
 * A reference rooted at `focusEntry` / `focusGrid` / `item` is a data
 * dependency too — on the page's BOUND ITEM rather than on a named list — and
 * comes back as a MARKER object (`{itemBinding: 'focusEntry'}`) at the end of
 * the array, in that fixed order. A marker is deliberately not a string: a
 * caller that wants selector names (`selectorsRead`, `readersOf`) must not be
 * able to take a bound item for one by accident, and `itemBindingsOf` is the
 * one way to read them back.
 */
export function selectorRefs(node, names) {
  const out = [];
  const bound = new Set();
  const take = (v) => {
    if (typeof v !== 'string') return;
    const root = v.split('.')[0].trim();
    if (names.has(root)) { if (!out.includes(root)) out.push(root); return; }
    if (ITEM_BINDING_SET.has(root)) bound.add(root);
  };
  const agg = (spec) => {
    if (!spec || typeof spec !== 'object') return;
    take(spec.of);
    take(spec.from);
  };
  /** The typed envelopes, anywhere under a prop — arrays and objects alike. */
  const scan = (v) => {
    if (!v || typeof v !== 'object') return;
    if (Array.isArray(v)) { v.forEach(scan); return; }
    agg(v.agg);
    take(v.$);
    take(v.path);
    for (const inner of Object.values(v)) scan(inner);
  };
  if (typeof node?.bind === 'string') take(node.bind);
  else if (node?.bind && typeof node.bind === 'object') { take(node.bind.bind); agg(node.bind.agg); }
  if (typeof node?.when === 'string') take(node.when); else scan(node?.when);
  // `repeat` names the rows a node iterates (`focusEntry.topics`). It counts
  // for the BOUND ITEM only: widening the named-selector scan through it would
  // credit plant-expert's `attentionRows` with reading `attention`, which is a
  // change to the shipped data wires and not what this is about.
  const repeatRoot = typeof node?.repeat === 'string' ? node.repeat.split('.')[0].trim() : null;
  if (repeatRoot && !names.has(repeatRoot) && ITEM_BINDING_SET.has(repeatRoot)) bound.add(repeatRoot);
  for (const v of Object.values(node?.props || {})) { take(v); scan(v); }
  for (const b of ITEM_BINDINGS) if (bound.has(b)) out.push({ itemBinding: b });
  return out;
}

/** The bound-item roots in a `selectorRefs` result, as plain strings. */
export function itemBindingsOf(refs) {
  return (refs || [])
    .filter((r) => r && typeof r === 'object' && typeof r.itemBinding === 'string')
    .map((r) => r.itemBinding);
}

/**
 * Does this node SUPPLY an `item` scope of its own? A collection's card
 * template and a `repeat` both render once per row with `item` bound to that
 * row (`interpreter.jsx`), so `item.clip_url` inside `clipRail`'s card is the
 * RAIL's row — never the page's bound item. Without this, every grid in the
 * fleet would have read as depending on the detail page's focused item.
 */
function suppliesItemScope(node) {
  if (node?.repeat !== undefined && node?.repeat !== null) return true;
  return Boolean(node?.props?.card);
}

/**
 * WHICH BOUND ITEM A PAGE IS OPENED ON — `focusEntry` for an item page
 * (`page_args.item_id`), `focusGrid` for a session one (`page_args.session_id`),
 * null for an index page, which has no bound item at all and is why nothing
 * built on this changes an index page's reading.
 *
 * DECLARED FIRST: a root that binds it says so (scene-studio's `ItemPage`,
 * my-wardrobe's `SessionPage`) — the same fact `hostOpensPage` resolves a
 * destination page by, read from the page instead of from the slot.
 *
 * …but half the fleet does NOT declare it there: notes-taker, my-wardrobe and
 * social-media-studio root their `data_view` in a plain `PageShell` and let the
 * modules inside read `focusEntry.*` directly. A page whose modules render the
 * focused row IS bound to it, whatever its root element happens to be, so the
 * modules are the second reading.
 *
 * THE ENTRY PAGE IS NEVER BOUND, and that exclusion is doing real work rather
 * than being tidy: plant-expert's index carries two ASPCA links and
 * social-media-studio's eight generate buttons that mention `focusEntry` in a
 * payload, and an index page taking the detail-page reading is precisely what
 * must not happen. The entry page is the one the host opens with no arguments —
 * there is no row for it to be bound to.
 */
const PAGE_BINDINGS = new WeakMap();

export function pageFocusBinding(blueprint, page) {
  const p = typeof page === 'string'
    ? (blueprint?.pages || []).find((x) => x?.id === page)
    : page;
  if (!p || !blueprint) return null;
  // memoised per blueprint: the module scan below walks every component, and
  // this is asked once per module on every re-measure
  let cache = PAGE_BINDINGS.get(blueprint);
  if (!cache) { cache = new Map(); PAGE_BINDINGS.set(blueprint, cache); }
  if (cache.has(p.id)) return cache.get(p.id);
  const answer = resolvePageBinding(blueprint, p);
  cache.set(p.id, answer);
  return answer;
}

function resolvePageBinding(blueprint, p) {
  const root = blueprint?.components?.[p.root];
  const focusProp = typeof root?.props?.focus === 'string' ? root.props.focus.split('.')[0].trim() : null;
  const declared = bindOf(root) || focusProp;
  if (declared === 'focusEntry' || declared === 'focusGrid') return declared;
  if (p.entry) return null;
  const names = new Set(Object.keys(blueprint?.data?.selectors || {}));
  let entry = null;
  for (const [id, node] of Object.entries(blueprint?.components || {})) {
    const marks = itemBindingsOf(selectorRefs(node, names));
    const grid = marks.includes('focusGrid');
    if (!grid && !marks.includes('focusEntry')) continue;
    if (pageOfNode(blueprint, id)?.id !== p.id) continue;
    if (grid) return 'focusGrid';
    entry = entry || 'focusEntry';
  }
  return entry;
}

/**
 * THE BOUND-ITEM DEPENDENCY ONE MODULE TAKES, or null.
 *
 * Two conditions, and both are needed. The module has to READ the bound item
 * (`focusEntry.images` on scene-studio's carousel, `focusEntry.count` in a
 * `when`), and it has to be ON a page that HAS one. An index page has none, so
 * a grid there depends on its own selector exactly as before; a detail page has
 * one, so a bare `item.*` written on that page means that row.
 */
export function boundItemBinding(blueprint, nodeId) {
  if (!nodeId) return null;
  const pageBinding = pageFocusBinding(blueprint, pageOfNode(blueprint, nodeId));
  if (!pageBinding) return null;
  const node = blueprint?.components?.[declaredOwner(blueprint, nodeId)];
  if (!node) return null;
  const marks = itemBindingsOf(selectorRefs(node, new Set(Object.keys(blueprint?.data?.selectors || {}))));
  if (marks.includes('focusGrid')) return 'focusGrid';
  if (marks.includes('focusEntry')) return 'focusEntry';
  // a bare `item.*` is the page's row — unless this node is what supplies the
  // scope that word names
  if (marks.includes('item') && !suppliesItemScope(node)) return pageBinding;
  return null;
}

/**
 * The selector ids a module READS — its `bind`, its `when`, and every selector
 * reference anywhere in its props (`selectorRefs`). That is the whole contract:
 * a module with no connections still declares its data this way, which is what
 * makes the data zone worth drawing.
 *
 * A card instance (or a leaf inside a card template) reads `item.*` — the ROW
 * of the grid's bound selector — so the GRID's selectors are its data, and
 * `declaredOwner` is what says so.
 *
 * NAMES ONLY: a bound-item marker is not a selector id, and everything
 * downstream of this treats what it returns as one — `selectorLineage` walks it
 * as a key, `readersOf` inverts it into a Map, the data card prints it. The
 * bound item is read back through `boundItemBinding` instead, which is also
 * where the "is there a bound item on this page at all" question belongs.
 */
export function selectorsRead(blueprint, nodeId) {
  const node = blueprint?.components?.[declaredOwner(blueprint, nodeId)];
  if (!node) return [];
  return selectorRefs(node, new Set(Object.keys(blueprint?.data?.selectors || {})))
    .filter((ref) => typeof ref === 'string');
}

/**
 * Those selectors PLUS everything they read — `of` / `from` / `args` — walked
 * up to the root kinds, so highlighting one row lights the whole chain
 * (`totals ⟵ meals ⟵ food_entry`).
 */
export function selectorLineage(blueprint, ids) {
  const sel = blueprint?.data?.selectors || {};
  const seen = new Set();
  const walk = (id) => {
    if (!id || seen.has(id) || !sel[id]) return;
    seen.add(id);
    const spec = sel[id];
    for (const ref of [spec.of, spec.from, ...Object.values(spec.args || {})]) {
      if (typeof ref === 'string') walk(ref.split('.')[0]);
    }
  };
  (ids || []).forEach(walk);
  return seen;
}
